/* @jsx h */
import { computed, defineComponent, h, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { Loading, useLocalization } from "@lemmonhq/uikit";
import { useToast, useTrpc } from "@app/vue/providers/index";
import { ReceiptEditor } from "../Purchases/ReceiptEditor";
import type { MarketplaceOrder, OrderDraft, OrderSummary } from "./types";
import { poNumber } from "./types";
import { OrderStatusBadge } from "./OrderStatusBadge";
import { PurchaseOrderEditor } from "./PurchaseOrderEditor";
import {
  ScanDialog,
  scanToOrder,
  useScanRouting,
  type ScanResult,
} from "../Scan/index";
import { SubpageHeader } from "@app/vue/components/SubpageHeader";

type Tab = "all" | "open" | "received" | "marketplace";
const TABS: Tab[] = ["all", "open", "received", "marketplace"];

// One row per order, wherever it was placed.
type Row =
  | { kind: "po"; date: string; po: OrderSummary }
  | { kind: "marketplace"; date: string; mo: MarketplaceOrder };

const MARKETPLACE_STATUS: Record<string, { cls: string; key: string }> = {
  new: { cls: "badge-info badge-outline", key: "inventory.orders.mp_new" },
  preparing: {
    cls: "badge-warning badge-outline",
    key: "inventory.orders.mp_preparing",
  },
  ready: {
    cls: "badge-success badge-outline",
    key: "inventory.orders.mp_ready",
  },
  closed: { cls: "badge-success", key: "inventory.orders.mp_closed" },
};

const formatDay = (iso: string | null) =>
  iso
    ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString()
    : "—";

// Orders: purchase orders to outside suppliers and Lemmon Marketplace orders.
// See docs/plans/inventory_and_costing/05_purchasing.md.
export const InventoryOrdersPage = defineComponent({
  name: "InventoryOrdersPage",
  setup() {
    const { t } = useLocalization();
    const trpc = useTrpc();
    const toast = useToast();
    const route = useRoute();
    const router = useRouter();

    const loading = ref(true);
    const tab = ref<Tab>(
      route.query.source === "marketplace" ? "marketplace" : "all",
    );
    const orders = ref<OrderSummary[]>([]);
    const marketplace = ref<MarketplaceOrder[]>([]);
    // undefined = closed, null = new, number = open that order.
    const editing = ref<number | null | undefined>(undefined);
    const draft = ref<OrderDraft | null>(null);
    const receiving = ref<number | null>(null);

    const { scanning, onOpen } = useScanRouting(
      "order",
      (result: ScanResult) => {
        draft.value = scanToOrder(result);
        editing.value = null;
      },
    );

    const load = async () => {
      const [pos, mos] = await Promise.all([
        trpc.purchaseOrders.list.query({ limit: 200, offset: 0 }),
        trpc.marketplace.listPurchaseOrders.query().catch(() => []),
      ]);
      orders.value = pos as OrderSummary[];
      marketplace.value = mos as MarketplaceOrder[];
    };

    onMounted(async () => {
      try {
        await load();
        const open = Number(route.query.open);
        if (open) editing.value = open;
      } catch {
        toast.showToast(t("inventory.orders.load_failed"), { type: "error" });
      } finally {
        loading.value = false;
      }
    });

    const rows = computed<Row[]>(() => {
      const po: Row[] = orders.value
        .filter((o) =>
          tab.value === "all"
            ? true
            : tab.value === "open"
              ? ["draft", "sent", "partially_received"].includes(o.status)
              : tab.value === "received"
                ? o.status === "received"
                : false,
        )
        .map((o) => ({ kind: "po", date: o.ordered_at, po: o }));
      const mo: Row[] = marketplace.value
        .filter((o) =>
          tab.value === "all" || tab.value === "marketplace"
            ? true
            : tab.value === "open"
              ? o.status !== "closed"
              : o.status === "closed",
        )
        .map((o) => ({ kind: "marketplace", date: o.created_at ?? "", mo: o }));
      return [...po, ...mo].sort((a, b) => b.date.localeCompare(a.date));
    });

    const counts = computed(() => ({
      open: orders.value.filter((o) =>
        ["draft", "sent", "partially_received"].includes(o.status),
      ).length,
      awaiting: orders.value.filter(
        (o) => o.status === "sent" || o.status === "partially_received",
      ).length,
      openValue: orders.value
        .filter((o) => ["sent", "partially_received"].includes(o.status))
        .reduce((s, o) => s + o.total, 0),
      marketplace: marketplace.value.filter((o) => o.status !== "closed")
        .length,
    }));

    const receive = async (o: OrderSummary) => {
      try {
        const { receipt_id } = await trpc.purchaseOrders.receive.mutate({
          id: o.id,
        });
        receiving.value = receipt_id;
      } catch (e) {
        toast.showToast(
          (e as { message?: string })?.message ??
            t("inventory.orders.save_failed"),
          { type: "error" },
        );
      }
    };

    const itemsLine = (names: string[]) =>
      names.length > 2
        ? `${names.slice(0, 2).join(", ")} ${t("inventory.orders.and_more").replace("{count}", String(names.length - 2))}`
        : names.join(", ");

    const renderPoRow = (o: OrderSummary) => (
      <tr
        key={`po-${o.id}`}
        class="cursor-pointer hover:bg-base-200/60"
        onClick={() => (editing.value = o.id)}
      >
        <td>
          <div class="flex items-center gap-2">
            <span class="font-semibold">
              {o.supplier_name ?? t("inventory.purchases.no_supplier")}
            </span>
            <span class="badge badge-ghost badge-xs font-mono">
              {poNumber(o.order_number)}
            </span>
            {o.has_document && (
              <span class="material-symbols-outlined text-sm text-base-content/40">
                attach_file
              </span>
            )}
          </div>
          <div class="mt-0.5 max-w-md truncate text-xs text-base-content/50">
            {itemsLine(o.item_names ?? [])}
          </div>
        </td>
        <td>
          <span class="badge badge-sm badge-outline whitespace-nowrap">
            {t("inventory.orders.source_supplier")}
          </span>
        </td>
        <td class="whitespace-nowrap">{formatDay(o.ordered_at)}</td>
        <td class="whitespace-nowrap">{formatDay(o.expected_at)}</td>
        <td class="text-right font-medium tabular-nums">
          {o.total.toFixed(2)}
        </td>
        <td>
          <OrderStatusBadge status={o.status} />
        </td>
        <td class="text-right">
          {(o.status === "sent" ||
            o.status === "partially_received" ||
            o.draft_receipt_id) &&
            o.status !== "cancelled" && (
              <button
                class="btn btn-primary btn-xs"
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  receive(o);
                }}
              >
                <span class="material-symbols-outlined text-sm">inventory</span>
                {t("inventory.orders.receive")}
              </button>
            )}
        </td>
      </tr>
    );

    const renderMarketplaceRow = (o: MarketplaceOrder) => {
      const st = MARKETPLACE_STATUS[o.status] ?? {
        cls: "badge-ghost",
        key: "",
      };
      const names = o.items.map((i) => i.name).filter(Boolean) as string[];
      return (
        <tr key={`mp-${o.id}`}>
          <td>
            <div class="flex items-center gap-2">
              <span class="font-semibold">{o.seller_name}</span>
              {o.display_id && (
                <span class="badge badge-ghost badge-xs font-mono">
                  #{o.display_id}
                </span>
              )}
            </div>
            <div class="mt-0.5 max-w-md truncate text-xs text-base-content/50">
              {itemsLine(names)}
            </div>
          </td>
          <td>
            <span class="badge badge-sm badge-primary badge-outline whitespace-nowrap">
              <span class="material-symbols-outlined text-[14px]">
                storefront
              </span>
              {t("inventory.orders.source_marketplace")}
            </span>
          </td>
          <td class="whitespace-nowrap">{formatDay(o.created_at)}</td>
          <td>—</td>
          <td class="text-right font-medium tabular-nums">
            {o.amount.toFixed(2)}
          </td>
          <td>
            <span class={`badge badge-sm ${st.cls}`}>
              {st.key ? t(st.key) : o.status}
            </span>
          </td>
          <td />
        </tr>
      );
    };

    const stat = (label: string, value: string, hint: string) => (
      <div class="rounded-xl border border-base-300 bg-base-100 p-4">
        <div class="text-xs font-medium uppercase tracking-wide text-base-content/50">
          {label}
        </div>
        <div class="mt-1 text-2xl font-semibold tabular-nums">{value}</div>
        <div class="text-xs text-base-content/50">{hint}</div>
      </div>
    );

    return () => (
      <div class="flex h-full w-full flex-col overflow-hidden bg-base-200">
        <SubpageHeader
          title={t("inventory.nav.orders")}
          subtitle={t("inventory.orders.subtitle")}
        >
          <div class="flex flex-wrap gap-2">
            <button
              class="btn btn-ghost btn-sm"
              onClick={() => router.push("/app/inventory/marketplace")}
            >
              <span class="material-symbols-outlined text-base">
                storefront
              </span>
              {t("inventory.orders.browse_marketplace")}
            </button>
            <button
              class="btn btn-primary btn-sm"
              onClick={() => (scanning.value = true)}
            >
              <span class="material-symbols-outlined text-base">
                document_scanner
              </span>
              {t("inventory.scan.scan_order")}
            </button>
            <button
              class="btn btn-neutral btn-sm"
              onClick={() => {
                draft.value = null;
                editing.value = null;
              }}
            >
              <span class="material-symbols-outlined text-base">add</span>
              {t("inventory.orders.new")}
            </button>
          </div>
        </SubpageHeader>

        <div class="flex-1 space-y-3 overflow-auto p-3">
          <div class="grid grid-cols-2 gap-3 lg:grid-cols-3">
            {stat(
              t("inventory.orders.stat_awaiting"),
              String(counts.value.awaiting),
              t("inventory.orders.stat_awaiting_hint"),
            )}
            {stat(
              t("inventory.orders.stat_open_value"),
              counts.value.openValue.toFixed(2),
              t("inventory.orders.stat_open_value_hint"),
            )}
            {stat(
              t("inventory.orders.stat_marketplace"),
              String(counts.value.marketplace),
              t("inventory.orders.stat_marketplace_hint"),
            )}
          </div>
          <div role="tablist" class="tabs tabs-boxed inline-flex bg-base-100">
            {TABS.map((k) => (
              <button
                key={k}
                role="tab"
                class={["tab tab-sm", tab.value === k ? "tab-active" : ""].join(
                  " ",
                )}
                onClick={() => (tab.value = k)}
              >
                {t(`inventory.orders.tab_${k}`)}
              </button>
            ))}
          </div>
          {loading.value ? (
            <div class="flex justify-center p-10">
              <Loading />
            </div>
          ) : rows.value.length === 0 ? (
            <div class="flex flex-col items-center justify-center gap-2 py-16 text-center">
              <span class="material-symbols-outlined text-5xl text-base-content/30">
                shopping_cart
              </span>
              <p class="text-sm text-base-content/60">
                {t("inventory.orders.empty")}
              </p>
            </div>
          ) : (
            <div class="overflow-x-auto rounded-lg border border-base-300 bg-base-100">
              <table class="table table-sm">
                <thead>
                  <tr>
                    <th>{t("inventory.purchases.supplier")}</th>
                    <th>{t("inventory.orders.col_source")}</th>
                    <th>{t("inventory.orders.col_ordered_on")}</th>
                    <th>{t("inventory.orders.expected_at")}</th>
                    <th class="text-right">
                      {t("inventory.purchases.col_total")}
                    </th>
                    <th>{t("inventory.purchases.col_status")}</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.value.map((r) =>
                    r.kind === "po"
                      ? renderPoRow(r.po)
                      : renderMarketplaceRow(r.mo),
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {scanning.value && (
          <ScanDialog
            kind="order"
            onClose={() => (scanning.value = false)}
            onOpen={onOpen}
          />
        )}
        {editing.value !== undefined && (
          <PurchaseOrderEditor
            orderId={editing.value}
            initial={draft.value}
            onClose={async (changed: boolean) => {
              editing.value = undefined;
              draft.value = null;
              if (route.query.open) router.replace({ query: {} });
              if (changed) await load();
            }}
            onReceive={(receiptId: number) => {
              editing.value = undefined;
              receiving.value = receiptId;
            }}
          />
        )}
        {receiving.value != null && (
          <ReceiptEditor
            receiptId={receiving.value}
            onClose={async () => {
              receiving.value = null;
              await load();
            }}
          />
        )}
      </div>
    );
  },
});
